<template>
  <div class="container grading-policy-container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>إدارة سياسات التقييم (وصفة حساب الدرجات)</h2>
          <el-button type="primary" @click="handleCreate">
            <el-icon><Plus /></el-icon> إضافة سياسة جديدة
          </el-button>
        </div>
      </template>

      <!-- --- الشرح الجديد هنا --- -->
      <el-collapse v-model="activeCollapse" style="margin-bottom: 20px;">
        <el-collapse-item name="1">
            <template #title>
                <el-icon><QuestionFilled /></el-icon><span style="margin-right: 8px; font-weight: bold;">لماذا هذه الواجهة مهمة؟ (اضغط للتوضيح)</span>
            </template>
            <div>
                <p>هذه الواجهة هي "العقل المدبر" للنظام الأكاديمي. هنا، نقوم بتحديد <strong>"وصفة"</strong> أو المعادلة التي سيستخدمها النظام تلقائيًا لحساب الدرجة النهائية لكل مادة.</p>
                <p><strong>مثال عملي:</strong> يمكنك إنشاء سياسة باسم "سياسة تقييم المرحلة الإعدادية" وتحديد أن الدرجة النهائية تتكون من:</p>
                <ul>
                    <li><strong>40%</strong> من متوسط درجات "الواجبات والمشاريع".</li>
                    <li><strong>20%</strong> من درجة "الامتحان النصفي".</li>
                    <li><strong>40%</strong> من درجة "الامتحان النهائي".</li>
                </ul>
                <p>عندما تقوم لاحقًا بحساب النتائج، سيقوم النظام بتطبيق هذه الوصفة تلقائيًا على جميع مواد المرحلة الإعدادية، مما يضمن الدقة والاتساق.</p>
            </div>
        </el-collapse-item>
      </el-collapse>

      <!-- جدول عرض السياسات الحالية -->
      <el-table :data="policies" v-loading="loading" border style="width: 100%">
        <el-table-column prop="name" label="اسم السياسة" min-width="200" />
        <el-table-column label="نطاق التطبيق" min-width="250">
          <template #default="{ row }">
            <div><strong>السنة:</strong> {{ row.study_year?.name || 'N/A' }}</div>
            <div v-if="row.course_offering" class="scope-tag scope-tag-specific">
                <strong>مقرر محدد:</strong> {{ row.course_offering.subject?.name }} - {{ row.course_offering.school_class?.name }}
            </div>
            <template v-else>
                <div v-if="row.grade_level" class="scope-tag"><strong>المستوى:</strong> {{ row.grade_level.name }}</div>
                <div v-if="row.subject" class="scope-tag"><strong>المادة:</strong> {{ row.subject.name }}</div>
            </template>
          </template>
        </el-table-column>
        <el-table-column label="المكونات (الأوزان %)" min-width="300">
            <template #default="{ row }">
                <el-tag v-for="comp in row.components" :key="comp.id" style="margin: 2px;">
                    {{ comp.assessment_type?.name || 'نوع محذوف' }}: {{ comp.weight_percentage }}%
                </el-tag>
            </template>
        </el-table-column>
        <el-table-column label="افتراضية؟" width="100">
            <template #default="{ row }">
                <el-tag v-if="row.is_default_for_school" type="success">نعم</el-tag>
                <el-tag v-else type="info">لا</el-tag>
            </template>
        </el-table-column>
        <el-table-column label="الإجراءات" width="150" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="handleEdit(row)">تعديل</el-button>
            <el-popconfirm title="هل أنت متأكد من حذف هذه السياسة؟" @confirm="handleDelete(row.id)">
              <template #reference><el-button size="small" type="danger">حذف</el-button></template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- Pagination -->
      <div class="pagination-container">
        <el-pagination
          v-if="pagination.total > 0"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          v-model:currentPage="pagination.current_page"
          :page-sizes="[10, 20, 50]"
          v-model:page-size="pagination.per_page"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
          background
        />
      </div>
    </el-card>

    <!-- Dialog لإنشاء/تعديل السياسة -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="50%" :close-on-click-modal="false">
      <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
        <el-form-item label="اسم السياسة" prop="name">
          <el-input v-model="form.name" placeholder="مثال: سياسة تقييم المرحلة الثانوية 2024" />
        </el-form-item>
        <el-form-item label="الوصف (اختياري)" prop="description">
          <el-input v-model="form.description" type="textarea" :rows="2" />
        </el-form-item>
        <el-divider>نطاق تطبيق السياسة</el-divider>
        <p class="form-tip">حدد نطاق السياسة. يمكنك جعلها عامة (للسنة الدراسية فقط) أو تحديدها لمستوى أو مادة أو مقرر معين.</p>
        <el-row :gutter="20">
            <el-col :span="8">
                <el-form-item label="السنة الدراسية" prop="study_year_id">
                    <el-select v-model="form.study_year_id" placeholder="مطلوب" style="width:100%"><el-option v-for="item in studyYears" :key="item.id" :label="item.name" :value="item.id" /></el-select>
                </el-form-item>
            </el-col>
            <el-col :span="8">
                <el-form-item label="المستوى الدراسي (اختياري)">
                    <el-select v-model="form.grade_level_id" placeholder="لكل المستويات" clearable style="width:100%"><el-option v-for="item in gradeLevels" :key="item.id" :label="item.name" :value="item.id" /></el-select>
                </el-form-item>
            </el-col>
            <el-col :span="8">
                <el-form-item label="المادة الدراسية (اختياري)">
                    <el-select v-model="form.subject_id" placeholder="لكل المواد" clearable style="width:100%"><el-option v-for="item in subjects" :key="item.id" :label="item.name" :value="item.id" /></el-select>
                </el-form-item>
            </el-col>
        </el-row>
        <el-form-item label="تطبيق على مقرر دراسي محدد (الأكثر تخصيصًا - اختياري)">
          <el-select
            v-model="form.course_offering_id"
            filterable
            remote
            :remote-method="searchCourseOfferings"
            :loading="courseOfferingsLoading"
            placeholder="لكل المقررات ضمن النطاق أعلاه"
            clearable
            style="width: 100%;"
          >
            <el-option v-for="item in courseOfferings" :key="item.id" :label="formatCourseOfferingLabel(item)" :value="item.id" />
          </el-select>
        </el-form-item>
        <el-divider>مكونات السياسة (يجب أن يكون المجموع 100%)</el-divider>
        <div v-for="(component, index) in form.components" :key="index" class="component-row">
            <el-form-item :prop="'components.' + index + '.assessment_type_id'" :rules="{ required: true, message: 'النوع مطلوب', trigger: 'change' }">
                <el-select v-model="component.assessment_type_id" placeholder="نوع التقييم" style="width: 250px;">
                    <el-option v-for="type in assessmentTypes" :key="type.id" :label="type.name" :value="type.id" />
                </el-select>
            </el-form-item>
            <el-form-item :prop="'components.' + index + '.weight_percentage'" :rules="{ required: true, message: 'الوزن مطلوب', trigger: 'blur' }">
                 <el-input-number v-model="component.weight_percentage" :min="0" :max="100" :step="5" placeholder="الوزن %" />
            </el-form-item>
            <el-button type="danger" circle @click="removeComponent(index)"><el-icon><Delete /></el-icon></el-button>
        </div>
        <el-button @click="addComponent" style="margin-top: 10px;"><el-icon><Plus /></el-icon> إضافة مكون</el-button>
        <div v-if="totalWeight !== 100" class="total-weight-error">
            المجموع الحالي: {{ totalWeight }}% (يجب أن يكون 100%)
        </div>
        <el-divider />
        <el-form-item>
            <el-checkbox v-model="form.is_default_for_school" label="جعل هذه السياسة هي الافتراضية؟" border />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">إلغاء</el-button>
          <el-button type="primary" @click="submitForm" :loading="formLoading" :disabled="totalWeight !== 100">حفظ السياسة</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage, ElPopconfirm } from 'element-plus';
import { Plus, Delete, QuestionFilled } from '@element-plus/icons-vue';

export default {
  name: 'GradingPolicyManagement',
  components: { Plus, Delete, ElPopconfirm, QuestionFilled },
  data() {
    return {
      api: null,
      policies: [],
      loading: false,
      dialogVisible: false,
      formLoading: false,
      activeCollapse: '',
      form: this.getInitialForm(),
      rules: {
        name: [{ required: true, message: 'اسم السياسة مطلوب', trigger: 'blur' }],
        study_year_id: [{ required: true, message: 'السنة الدراسية مطلوبة', trigger: 'change' }],
      },
      studyYears: [],
      gradeLevels: [],
      subjects: [],
      assessmentTypes: [],
      courseOfferings: [],
      courseOfferingsLoading: false,
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
      },
    };
  },
  computed: {
    dialogTitle() {
      return this.form.id ? 'تعديل سياسة التقييم' : 'إنشاء سياسة تقييم جديدة';
    },
    totalWeight() {
      if (!this.form.components) return 0;
      return this.form.components.reduce((sum, comp) => sum + (Number(comp.weight_percentage) || 0), 0);
    }
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
    
    this.fetchPolicies();
    this.fetchFormData();
  },
  methods: {
    getInitialForm() {
      return { 
        id: null, name: '', description: '', study_year_id: null, 
        grade_level_id: null, subject_id: null, course_offering_id: null, 
        is_default_for_school: false, 
        components: [{ assessment_type_id: null, weight_percentage: 100 }] 
      };
    },
    async fetchPolicies() {
      this.loading = true;
      try {
        const params = {
            page: this.pagination.current_page,
            per_page: this.pagination.per_page,
        };
        const response = await this.api.get('/grading-policies', { params });
        this.policies = response.data.data;
        this.pagination.total = response.data.total;
        this.pagination.current_page = response.data.current_page;
        this.pagination.per_page = Number(response.data.per_page);
      } catch (error) {
        ElMessage.error('Failed to fetch grading policies.');
      } finally {
        this.loading = false;
      }
    },
    async fetchFormData() {
      try {
        const [studyYearsRes, gradeLevelsRes, subjectsRes, assessmentTypesRes] = await Promise.all([
          this.api.get('/study-years'),
          this.api.get('/grade-levels'),
          this.api.get('/subjects', { params: { per_page: 1000 } }),
          this.api.get('/assessment-types'),
        ]);
        this.studyYears = studyYearsRes.data.data || studyYearsRes.data;
        this.gradeLevels = gradeLevelsRes.data.data || gradeLevelsRes.data;
        this.subjects = subjectsRes.data.data || subjectsRes.data;
        this.assessmentTypes = assessmentTypesRes.data;
      } catch (error) {
        ElMessage.error('Failed to fetch required data for the form.');
      }
    },
    async searchCourseOfferings(query) {
      if (!this.form.study_year_id) {
          ElMessage.warning('Please select a study year first to search for courses.');
          return;
      }
      this.courseOfferingsLoading = true;
      try {
          const params = { 
              search: query, 
              per_page: 50,
              study_year_id: this.form.study_year_id,
              grade_level_id: this.form.grade_level_id,
              subject_id: this.form.subject_id,
          };
          const response = await this.api.get('/course-offerings', { params });
          this.courseOfferings = response.data.data;
      } catch(error) {
          ElMessage.error('Failed to search course offerings.');
      } finally {
          this.courseOfferingsLoading = false;
      }
    },
    formatCourseOfferingLabel(course) {
        if (!course) return '';
        let label = course.subject?.name || 'مادة';
        if (course.school_class) {
            label += ` - ${course.school_class.name}`;
        }
        return label;
    },
    addComponent() {
      this.form.components.push({ assessment_type_id: null, weight_percentage: 0 });
    },
    removeComponent(index) {
      if (this.form.components.length > 1) {
        this.form.components.splice(index, 1);
      } else {
        ElMessage.warning('At least one component is required.');
      }
    },
    handleCreate() {
      this.form = this.getInitialForm();
      this.courseOfferings = [];
      this.dialogVisible = true;
    },
    handleEdit(policy) {
      this.form = JSON.parse(JSON.stringify(policy));
      if(this.form.course_offering_id && policy.course_offering) {
          this.courseOfferings = [policy.course_offering];
      } else {
          this.courseOfferings = [];
      }
      this.dialogVisible = true;
    },
    async submitForm() {
      if (!this.$refs.formRef) return;
      await this.$refs.formRef.validate(async (valid) => {
        if (!valid) {
          ElMessage.error('Please fill all required fields correctly.');
          return;
        }
        if (Math.abs(this.totalWeight - 100) > 0.01) {
            ElMessage.error('The sum of weight percentages must be exactly 100.');
            return;
        }
        this.formLoading = true;
        try {
          if (this.form.id) {
            await this.api.put(`/grading-policies/${this.form.id}`, this.form);
            ElMessage.success('Policy updated successfully!');
          } else {
            await this.api.post('/grading-policies', this.form);
            ElMessage.success('Policy created successfully!');
          }
          this.dialogVisible = false;
          this.fetchPolicies();
        } catch (error) {
          const errorMsg = error.response?.data?.errors ? Object.values(error.response.data.errors)[0][0] : (error.response?.data?.message || 'Operation failed.');
          ElMessage.error(errorMsg);
        } finally {
          this.formLoading = false;
        }
      });
    },
    async handleDelete(id) {
      try {
        await this.api.delete(`/grading-policies/${id}`);
        ElMessage.success('Policy deleted successfully!');
        this.fetchPolicies();
      } catch (error) {
        const errorMsg = error.response?.data?.message || 'Failed to delete policy.';
        ElMessage.error(errorMsg);
      }
    },
    handleSizeChange(val) {
      this.pagination.per_page = val;
      this.pagination.current_page = 1;
      this.fetchPolicies();
    },
    handleCurrentChange(val) {
      this.pagination.current_page = val;
      this.fetchPolicies();
    },
  },
};
</script>

<style scoped>
.grading-policy-container { padding: 20px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
.scope-tag { font-size: 0.8em; color: #606266; }
.scope-tag-specific { font-weight: bold; color: #409eff; }
.component-row { display: flex; gap: 10px; align-items: center; margin-bottom: 22px; }
.total-weight-error { color: #f56c6c; margin-top: 10px; font-weight: bold; }
.dialog-footer { display: flex; justify-content: flex-end; }
.form-tip { font-size: 0.85em; color: #909399; margin-top: -10px; margin-bottom: 15px; }
.pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 20px;
}
</style>
